import { useState } from 'react';

import { getShareableAttributes } from './shareable_attributes';

import type { Contact, Role, Visibility } from 'bundles/backoffice/contacts/types';
import type { ForRecordOption, ForRecordOptions, NewContactRole } from './types';

type Step = 'form' | 'selectContactAttributes' | 'selectEmployerAttributes';

interface Values {
  contact: Contact | null;
  employer: Contact | null;
  forRecordOption?: ForRecordOption;
  role: Role | null;
  visibility?: Visibility;
}

interface Options {
  forRecordOptions?: ForRecordOptions;
  roleOptions?: Role[];
  visibilityOptions?: Visibility[];
  onComplete: (contactRole: NewContactRole) => unknown;
}

export type AddContactWorkflow = ReturnType<typeof useAddContactWorkflow>;

export function useAddContactWorkflow({ forRecordOptions, roleOptions, visibilityOptions, onComplete }: Options) {
  const initialValues: Values = {
    contact: null,
    employer: null,
    forRecordOption: forRecordOptions?.options[0],
    role: roleOptions?.length === 1 ? roleOptions[0] : null,
    visibility: visibilityOptions?.[0],
  };

  const [values, setValues] = useState(initialValues);
  const [step, setStep] = useState<Step>('form');
  const [contactSharedAttributes, setContactSharedAttributes] = useState<string[]>([]);
  const [employerSharedAttributes, setEmployerSharedAttributes] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const { contact, employer, forRecordOption, role, visibility } = values;
  const canSave = Boolean(contact && (role || !roleOptions?.length));

  function change(changes: Partial<Values>) {
    setValues(previous => ({ ...previous, ...changes }));
    setErrors([]);
  }

  function reset() {
    setValues(initialValues);
    setStep('form');
    setContactSharedAttributes([]);
    setEmployerSharedAttributes([]);
    setErrors([]);
  }

  // Resolves to true only when the contact role has been built and accepted by onComplete;
  // advancing to a confirmation step resolves false, as does a rejected onComplete.
  async function save() {
    if (!contact || !canSave) {
      return false;
    }

    if (step === 'form' && needsAttributeSelection(contact)) {
      setStep('selectContactAttributes');
      return false;
    }

    if (step !== 'selectEmployerAttributes' && needsAttributeSelection(employer)) {
      setStep('selectEmployerAttributes');
      return false;
    }

    const contactRole: NewContactRole = {
      contact,
      employer,
      forRecordOption,
      role: role?.value,
      roleLabel: role?.label,
      visibility,
    };

    if (needsAttributeSelection(contact)) {
      contactRole.sharedAttributes = contactSharedAttributes;
    }

    if (employer && needsAttributeSelection(employer)) {
      contactRole.employer = { ...employer, sharedAttributes: employerSharedAttributes };
    }

    try {
      await onComplete(contactRole);
      return true;
    } catch (errors) {
      setErrors(Array.isArray(errors) ? errors : ['Something went wrong.']);
      return false;
    }
  }

  function needsAttributeSelection(contact: Contact | null) {
    if (contact?.private && visibility !== 'private') {
      return getShareableAttributes(contact).length > 0;
    } else {
      return false;
    }
  }

  let selectingAttributesFor: Contact | null = null;
  let setSharedAttributes: ((attributes: string[]) => void);
  let sharedAttributes: string[];
  if (step === 'selectEmployerAttributes') {
    selectingAttributesFor = employer;
    setSharedAttributes = setEmployerSharedAttributes;
    sharedAttributes = employerSharedAttributes;
  } else {
    if (step === 'selectContactAttributes') {
      selectingAttributesFor = contact;
    }

    setSharedAttributes = setContactSharedAttributes;
    sharedAttributes = contactSharedAttributes;
  }

  return {
    canSave,
    change,
    contact,
    employer,
    errors,
    forRecordOption,
    forRecordOptions,
    reset,
    role,
    roleOptions,
    save,
    selectingAttributesFor,
    setSharedAttributes,
    sharedAttributes,
    step,
    visibility,
    visibilityOptions,
  };
}
